import { useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/**
 * The guest whose access would end, and what they would lose: the assessment
 * they were asked to complete, or the assessments shared with them to read.
 */
export type GuestAccessToEnd =
  | { kind: "complete_assessment"; guestName: string }
  | { kind: "read_shared_assessments"; guestName: string; sharedCount: number };

/**
 * Whether the ending landed. One `failed` for every way it did not: nothing
 * whoever confirms could do differently tells a refusal from an unreachable
 * server.
 */
export type GuestAccessEndingOutcome = "ended" | "failed";

/**
 * Asks to confirm ending a guest's access, naming the guest and what they
 * lose, and ends it. No reason is asked for.
 */
export const EndGuestAccessDialog = ({
  guest,
  onClose,
  onEnd,
}: {
  /** The guest whose access would end, or `null` while the dialog is closed. */
  guest: GuestAccessToEnd | null;
  onClose: () => Promisable<void>;
  onEnd: () => Promisable<GuestAccessEndingOutcome>;
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"ending" | "failed" | undefined>(
    undefined,
  );
  const ending = status === "ending";

  const close = () => {
    setStatus(undefined);
    void onClose();
  };

  const end = async () => {
    setStatus("ending");
    let outcome: GuestAccessEndingOutcome;

    try {
      outcome = await onEnd();
    } catch {
      outcome = "failed";
    }

    if (outcome === "ended") {
      close();
      return;
    }

    setStatus("failed");
  };

  const loses =
    guest === null
      ? undefined
      : match(guest)
          .with({ kind: "complete_assessment" }, ({ guestName }) =>
            t("endGuestAccess.loses.complete_assessment", { name: guestName }),
          )
          .with(
            { kind: "read_shared_assessments" },
            ({ guestName, sharedCount }) =>
              t("endGuestAccess.loses.read_shared_assessments", {
                name: guestName,
                count: sharedCount,
              }),
          )
          .exhaustive();

  return (
    <Dialog
      open={guest !== null}
      // Nothing closes the dialog while the ending is in flight: it cannot
      // be called back once sent.
      onClose={ending ? undefined : close}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {guest === null
          ? null
          : t("endGuestAccess.title", { name: guest.guestName })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>{loses}</DialogContentText>
          {status === "failed" ? (
            <Alert severity="error">{t("endGuestAccess.failed")}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          label: t("endGuestAccess.cancel"),
          onClick: close,
          disabled: ending,
        }}
        proceed={{
          label: t("endGuestAccess.confirm"),
          onClick: end,
          disabled: ending,
          kind: "destructive",
        }}
      />
    </Dialog>
  );
};
